import React, { useState } from "react";
import { isAxiosError } from "axios";
import { GrantsLabelItem } from "../../../services/grantsApi";
import { LABEL_COLORS, getColorConfig } from "./labelColors";

/** Whitelist of backend error messages safe to display to the user */
const SAFE_ERROR_MESSAGES = [
  'Osiągnięto limit 10 etykiet.',
  'Etykieta o tej nazwie już istnieje.',
  'Etykieta nie została znaleziona.',
  'Nieprawidłowy kolor.',
];

function getSafeErrorMessage(err: unknown): string {
  if (isAxiosError(err)) {
    const backendMsg = err.response?.data?.message;
    if (typeof backendMsg === 'string' && SAFE_ERROR_MESSAGES.includes(backendMsg)) {
      return backendMsg;
    }
  }
  return 'Wystąpił błąd. Spróbuj ponownie.';
}

interface LabelManagementPanelProps {
  labels: GrantsLabelItem[];
  onCreateLabel: (name: string, color: string) => Promise<any>;
  onUpdateLabel: (
    id: string,
    payload: { name?: string; color?: string }
  ) => Promise<any>;
  onDeleteLabel: (id: string) => Promise<void>;
}

const LabelManagementPanel: React.FC<LabelManagementPanelProps> = ({
  labels,
  onCreateLabel,
  onUpdateLabel,
  onDeleteLabel,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formName, setFormName] = useState("");
  const [formColor, setFormColor] = useState("green");
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const resetForm = () => {
    setEditingId(null);
    setFormName("");
    setFormColor("green");
    setFormError(null);
  };

  const startCreate = () => {
    resetForm();
    setEditingId("new");
  };

  const startEdit = (label: GrantsLabelItem) => {
    setEditingId(label.id);
    setFormName(label.name);
    setFormColor(label.color);
    setFormError(null);
  };

  const handleSave = async () => {
    if (!formName.trim()) {
      setFormError("Nazwa etykiety jest wymagana");
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      if (editingId === "new") {
        await onCreateLabel(formName.trim(), formColor);
      } else if (editingId) {
        await onUpdateLabel(editingId, {
          name: formName.trim(),
          color: formColor,
        });
      }
      resetForm();
    } catch (err: unknown) {
      setFormError(getSafeErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await onDeleteLabel(id);
      setConfirmDeleteId(null);
    } catch (err: unknown) {
      setFormError(getSafeErrorMessage(err));
    }
  };

  return (
    <div className="border-b border-neutral-200" data-testid="label-management-panel">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-2 flex items-center justify-between text-sm text-neutral-600 hover:bg-neutral-50"
      >
        <span className="font-medium">
          Etykiety ({labels.length}/10)
        </span>
        <svg
          className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`}
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path
            fillRule="evenodd"
            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="px-4 pb-3 space-y-2">
          {/* Existing labels */}
          {labels.map((label) => {
            const colorCfg = getColorConfig(label.color);
            return (
              <div
                key={label.id}
                className="flex items-center justify-between py-1"
              >
                <div className="flex items-center gap-2">
                  <span
                    className={`w-3 h-3 rounded-full ${colorCfg.dot}`}
                  />
                  <span className="text-sm text-neutral-900">
                    {label.name}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  {confirmDeleteId === label.id ? (
                    <>
                      <span className="text-xs text-red-600 mr-1">
                        Usunąć?
                      </span>
                      <button
                        onClick={() => handleDelete(label.id)}
                        className="text-xs text-red-600 hover:text-red-800 px-1"
                      >
                        Tak
                      </button>
                      <button
                        onClick={() => setConfirmDeleteId(null)}
                        className="text-xs text-neutral-500 hover:text-neutral-700 px-1"
                      >
                        Nie
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => startEdit(label)}
                        className="text-xs text-neutral-500 hover:text-neutral-700 px-1"
                      >
                        Edytuj
                      </button>
                      <button
                        onClick={() => setConfirmDeleteId(label.id)}
                        className="text-xs text-red-500 hover:text-red-700 px-1"
                      >
                        Usuń
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}

          {/* Empty state */}
          {labels.length === 0 && !editingId && (
            <p className="text-sm text-neutral-500 py-2">
              Nie masz jeszcze etykiet. Utwórz pierwszą!
            </p>
          )}

          {/* Create/Edit form */}
          {editingId ? (
            <div className="border border-neutral-200 rounded-md p-3 space-y-2 bg-neutral-50">
              <input
                type="text"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="Nazwa etykiety"
                maxLength={50}
                className="w-full text-sm border border-neutral-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary-500"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSave();
                  if (e.key === "Escape") resetForm();
                }}
              />

              {/* Color picker */}
              <div
                className="flex flex-wrap gap-1.5"
                role="radiogroup"
                aria-label="Wybierz kolor etykiety"
              >
                {LABEL_COLORS.map((color) => (
                  <button
                    key={color.key}
                    onClick={() => setFormColor(color.key)}
                    role="radio"
                    aria-checked={formColor === color.key}
                    aria-label={`Wybierz kolor ${color.name}`}
                    className={`w-6 h-6 rounded-full ${color.dot} ${
                      formColor === color.key
                        ? "ring-2 ring-offset-1 ring-primary-500"
                        : ""
                    }`}
                    title={color.name}
                  />
                ))}
              </div>

              {formError && (
                <p className="text-xs text-red-600">{formError}</p>
              )}

              <div className="flex gap-2">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="text-xs bg-primary-600 text-white px-3 py-1 rounded hover:bg-primary-700 disabled:opacity-50"
                >
                  {saving ? "Zapisuję..." : "Zapisz"}
                </button>
                <button
                  onClick={resetForm}
                  className="text-xs text-neutral-600 px-3 py-1 rounded hover:bg-neutral-200"
                >
                  Anuluj
                </button>
              </div>
            </div>
          ) : (
            labels.length < 10 && (
              <button
                onClick={startCreate}
                data-testid="add-label-button"
                className="text-xs text-primary-600 hover:text-primary-800 font-medium"
              >
                + Dodaj etykietę
              </button>
            )
          )}
        </div>
      )}
    </div>
  );
};

export default LabelManagementPanel;
